import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, getStockStatusColor } from '../utils/formatters';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useProposal } from '../contexts/ProposalContext';
import { FileText, Check } from 'lucide-react';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToProposal, isInProposal } = useProposal();
  
  // Use thumbnail from list API (primary image only)
  // Full images available on product detail page
  const thumbnail = product.thumbnail || (product.images?.find(img => img.is_primary) || product.images?.[0]);
  const inProposal = isInProposal(product.id);

  const handleAddToProposal = (e) => {
    e.stopPropagation();
    const added = addToProposal(product);
    if (added) {
      toast.success('Added to proposal');
    } else {
      toast.info('Already in proposal');
    }
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
    >
      <div 
        onClick={() => navigate(`/products/${product.id}`)}
        className="cursor-pointer"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-white flex items-center justify-center">
          {thumbnail ? (
            <img
              src={thumbnail.data}
              alt={product.name}
              className="!w-full !h-full object-contain group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
              <svg className="w-20 h-20 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-xs text-slate-400 font-medium">No image</p>
            </div>
          )}
          <div className="absolute top-3 right-3">
            <Badge className={`${getStockStatusColor(product.stock_status)} font-semibold`}>
              {product.stock_status}
            </Badge>
          </div>
        </div>
        <div className="p-5">
          <h3 className="font-barlow font-semibold text-lg text-slate-900 mb-2 line-clamp-2">
            {product.name}
          </h3>
          <p className="text-sm text-slate-500 mb-3">{product.category}</p>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-2xl font-bold text-consortium-green">
                {formatCurrency(product.recommended_selling_price)}
              </p>
              <p className="text-xs text-slate-500 mt-1">MOQ: {product.moq} units</p>
            </div>
          </div>
        </div>
      </div>
      <div className="px-5 pb-5">
        <Button
          onClick={handleAddToProposal}
          variant={inProposal ? "secondary" : "outline"}
          className="w-full gap-2"
          size="sm"
          data-testid={`add-to-proposal-${product.id}`}
        >
          {inProposal ? (
            <>
              <Check className="w-4 h-4" />
              In Proposal
            </>
          ) : (
            <>
              <FileText className="w-4 h-4" />
              Add to Proposal
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
 
